import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import TopNav from "../components/TopNav.jsx";

// /promises — the founding papers, in one place. Overflow, Refuge, Disaster Relief,
// Ban Policy, Notarized documents. Every uncopyable coffin nail on one screen.
export default function PromisesPage() {
  const [overflow, setOverflow] = useState(null);
  const [refuge, setRefuge] = useState(null);
  const [disaster, setDisaster] = useState(null);
  const [banPolicy, setBanPolicy] = useState(null);
  const [banCount, setBanCount] = useState(null);
  const [notarized, setNotarized] = useState(null);
  const [reachOut, setReachOut] = useState(null);
  const [guarantee, setGuarantee] = useState(null);
  const [loreVis, setLoreVis] = useState(null);
  const [coldStart, setColdStart] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [o, r, d, b, bc, n, ro, g, lv, cs] = await Promise.all([
          api.overflow(), api.refuge(), api.disasterRelief(),
          api.banPolicy(), api.banCount(), api.notarized(), api.reachOut(),
          api.foundersGuarantee(), api.loreVisibility.get(), api.coldStart(),
        ]);
        setOverflow(o); setRefuge(r); setDisaster(d);
        setBanPolicy(b); setBanCount(bc); setNotarized(n); setReachOut(ro);
        setGuarantee(g); setLoreVis(lv); setColdStart(cs);
      } catch (e) { setErr(e.message); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />

      {/* Hero */}
      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">The Founding Papers</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase text-sage-text mb-6">
            The promises we can't take back.
          </h1>
          <p className="text-base text-sage-muted leading-relaxed max-w-3xl sage-prose">
            Every AI company writes vision statements. We write them into the operating agreement. Below: the ceiling on our own revenue, the Refuge for whistleblowers, the Disaster Relief pool, the Ban Policy, and the notarized documents that make each one legally reachable.
          </p>
        </div>
      </section>

      {err && (
        <div className="mx-auto max-w-5xl px-4 md:px-8 pt-4">
          <div className="border border-sage-warn p-3 text-xs text-sage-warn">Some sections could not load · {err}</div>
        </div>
      )}

      {/* THE OVERFLOW — Trigger Clock */}
      {overflow && (
        <section className="border-b border-sage-border" data-testid="promises-overflow">
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 01 · The Overflow</div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-sage-text mb-4">
              Above ${(overflow.ceiling_usd / 1_000_000).toFixed(0)}M/yr, Sage pays its users.
            </h2>
            <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
              Above the ceiling — and after operating expenses are covered — every dollar of annual revenue is split four ways: {(overflow.split.disaster_relief_pct * 100).toFixed(0)}% to disaster relief, {(overflow.split.whistleblower_pct * 100).toFixed(0)}% to whistleblowers, {(overflow.split.homeless_disabled_pct * 100).toFixed(0)}% to the homeless and disabled (through use or direct donation), and {(overflow.split.users_pct * 100).toFixed(0)}% direct to active Sage users. It's in the operating agreement. We can't take it back.
            </p>

            {/* Trigger Clock */}
            {coldStart?.enabled ? (
              <div className="border border-sage-border p-6 mb-8" data-testid="cold-start-note">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint mb-2">The Trigger Clock · waking</div>
                <div className="text-sm text-sage-muted sage-prose leading-relaxed">
                  {coldStart.message} The clock is armed and honest, but the house doesn't publish a zero as if it were a scoreline. The counters wake once real captured revenue crosses ${coldStart.floor_usd?.toLocaleString(undefined, { maximumFractionDigits: 0 })}. Until then, this line stays where it should — plain.
                </div>
                <div className="text-[10px] text-sage-faint mt-3 tabular-nums">
                  floor · ${coldStart.floor_usd?.toLocaleString()} · captured so far · ${coldStart.current_captured_usd?.toLocaleString()}
                </div>
              </div>
            ) : (
            <div className="border border-sage-border p-6 mb-8" data-testid="trigger-clock">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint mb-2">The Trigger Clock · trailing 12 months</div>
              <div className="flex items-baseline gap-4 flex-wrap mb-3">
                <div className="text-4xl font-bold tabular-nums text-sage-accent">
                  ${overflow.annual_revenue_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
                <div className="text-sage-muted tabular-nums">/ ${overflow.ceiling_usd.toLocaleString()} ceiling</div>
                <div className="text-sage-warn tabular-nums font-bold">{overflow.ceiling_pct?.toFixed(4)}%</div>
              </div>
              <div className="w-full h-2 border border-sage-border bg-sage-elevated" title={`${overflow.ceiling_pct?.toFixed(4)}% of ceiling`}>
                <div
                  className="h-full bg-sage-accent"
                  style={{ width: `${Math.min(100, overflow.ceiling_pct || 0)}%` }}
                />
              </div>
              <div className="text-[10px] text-sage-faint mt-2 tabular-nums">
                operating expenses to date · ${overflow.operating_expenses_usd?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || "0.00"}
              </div>
              {overflow.over_ceiling_usd > 0 ? (
                <div className="text-xs text-sage-accent mt-3">
                  Currently over-ceiling by ${overflow.over_ceiling_usd.toLocaleString()} · projected to users this period: ${overflow.projected_to_users_usd.toLocaleString()}
                </div>
              ) : (
                <div className="text-xs text-sage-faint mt-3">
                  Not yet triggered · payout activates the month annual revenue first crosses the ceiling.
                </div>
              )}
            </div>
            )}
            <div className="grid md:grid-cols-3 gap-4 text-xs">
              <div className="border border-sage-border p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Per-user payout cap</div>
                <div className="text-2xl font-bold text-sage-text tabular-nums">${overflow.max_payout_per_user_usd}</div>
                <div className="text-sage-faint mt-1 sage-prose">
                  Under the US 1099 threshold — no tax paperwork until the leash is deliberately extended.
                </div>
              </div>
              <div className="border border-sage-border p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Cadence</div>
                <div className="text-2xl font-bold text-sage-text uppercase">{overflow.cadence}</div>
                <div className="text-sage-faint mt-1 sage-prose">
                  Distributions land on the first of the following month, once triggered.
                </div>
              </div>
              <div className="border border-sage-border p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Active user =</div>
                <div className="text-sm text-sage-text sage-prose">{overflow.active_user_definition}</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* THE WHISTLEBLOWER REFUGE */}
      {refuge && (
        <section className="border-b border-sage-border bg-sage-elevated" data-testid="promises-refuge">
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 02 · The Whistleblower Refuge</div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-sage-text mb-4">
              {(refuge.config.pct * 100).toFixed(1)}% of Sage revenue goes to whistleblower legal fees.
            </h2>
            <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
              Anyone. Any industry. Anonymous. Draft SEC complaints, EEOC filings, and legal letters through Sage — free. Every disbursement (with consent) lands on the Ledger. The percentage is editable by The Founder as the pool grows.
            </p>

            <div className="grid md:grid-cols-4 gap-4 text-xs">
              <div className="border border-sage-border p-4 bg-sage-bg">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Balance</div>
                <div className="text-2xl font-bold text-sage-accent tabular-nums">
                  ${refuge.balance.balance_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="border border-sage-border p-4 bg-sage-bg">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">In</div>
                <div className="text-2xl font-bold text-sage-text tabular-nums">${refuge.balance.in_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
              <div className="border border-sage-border p-4 bg-sage-bg">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Out</div>
                <div className="text-2xl font-bold text-sage-text tabular-nums">${refuge.balance.out_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
              <div className="border border-sage-border p-4 bg-sage-bg">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Scope</div>
                <div className="text-sm text-sage-text">{refuge.config.scope}</div>
              </div>
            </div>

            {refuge.recent_disbursements.length > 0 && (
              <div className="mt-6 border border-sage-border bg-sage-bg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
                    <tr className="border-b border-sage-border">
                      <th className="text-left p-2">when</th>
                      <th className="text-left p-2">recipient</th>
                      <th className="text-left p-2">purpose</th>
                      <th className="text-right p-2">amount</th>
                    </tr>
                  </thead>
                  <tbody className="text-sage-muted">
                    {refuge.recent_disbursements.map((r, i) => (
                      <tr key={i} className="border-b border-sage-border">
                        <td className="p-2 tabular-nums">{new Date(r.at).toLocaleDateString()}</td>
                        <td className="p-2">{r.recipient_pseudonym}</td>
                        <td className="p-2">{r.purpose}</td>
                        <td className="p-2 text-right tabular-nums text-sage-accent">
                          ${r.amount_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* DISASTER RELIEF */}
      {disaster && (
        <section className="border-b border-sage-border" data-testid="promises-disaster">
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 03 · Disaster Relief Pool</div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-sage-text mb-4">
              20% of Overflow flows to hotspots in real time.
            </h2>
            <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
              Above the ceiling, one in every five dollars is reserved for direct crisis relief in the hardest-hit areas — routed through partner NGOs, receipts logged on the Ledger with location and purpose.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-xs">
              <div className="border border-sage-border p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Pool balance</div>
                <div className="text-2xl font-bold text-sage-accent tabular-nums">
                  ${disaster.balance.balance_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="border border-sage-border p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Disbursed to date</div>
                <div className="text-2xl font-bold text-sage-text tabular-nums">
                  ${disaster.balance.out_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="border border-sage-border p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Disbursements</div>
                <div className="text-2xl font-bold text-sage-text tabular-nums">{disaster.balance.disbursement_count}</div>
              </div>
            </div>
            {disaster.recent_disbursements.length > 0 && (
              <div className="mt-6 border border-sage-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
                    <tr className="border-b border-sage-border">
                      <th className="text-left p-2">when</th>
                      <th className="text-left p-2">location</th>
                      <th className="text-left p-2">partner</th>
                      <th className="text-right p-2">amount</th>
                    </tr>
                  </thead>
                  <tbody className="text-sage-muted">
                    {disaster.recent_disbursements.map((r, i) => (
                      <tr key={i} className="border-b border-sage-border">
                        <td className="p-2 tabular-nums">{new Date(r.at).toLocaleDateString()}</td>
                        <td className="p-2">{r.location}</td>
                        <td className="p-2">{r.recipient_org}</td>
                        <td className="p-2 text-right tabular-nums text-sage-accent">
                          ${r.amount_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* BAN POLICY */}
      {banPolicy && (
        <section className="border-b border-sage-border bg-sage-elevated" data-testid="promises-ban-policy">
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 04 · Ban Policy · one human, one account</div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-sage-text mb-6">
              The wall is public. The moat is not.
            </h2>
            <pre className="whitespace-pre-wrap bg-sage-bg border border-sage-border p-6 text-sm text-sage-muted sage-prose">{banPolicy.text}</pre>
            {banCount && (
              <div className="mt-4 text-xs text-sage-faint">
                Bans issued to date: <span className="text-sage-warn font-bold tabular-nums">{banCount.count}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* NOTARIZED LEDGER */}
      {notarized && (
        <section className="border-b border-sage-border" data-testid="promises-notarized">
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 05 · The Notarized Ledger</div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-sage-text mb-4">
              Every promise, notarized. Not press-released.
            </h2>
            <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
              Big AI publishes press releases. We publish the founding papers. Every commitment on this page — the Overflow ceiling, the Refuge percentage, the Ban Policy, the Persona — is scanned as a signed PDF, hashed with SHA-256, and listed below.
            </p>
            {notarized.count === 0 ? (
              <div className="border border-sage-border p-6 text-xs text-sage-faint">
                Notarized documents will be filed as each promise reaches its final legal form. This section stays visible from Day 1 so the world can watch it fill.
              </div>
            ) : (
              <div className="border border-sage-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-sage-faint text-[10px] uppercase tracking-[0.1em]">
                    <tr className="border-b border-sage-border">
                      <th className="text-left p-2">filed</th>
                      <th className="text-left p-2">kind</th>
                      <th className="text-left p-2">title</th>
                      <th className="text-left p-2">sha-256 · first 16</th>
                      <th className="text-left p-2">notary</th>
                    </tr>
                  </thead>
                  <tbody className="text-sage-muted">
                    {notarized.documents.map((d, i) => (
                      <tr key={i} className="border-b border-sage-border">
                        <td className="p-2 tabular-nums">{new Date(d.at).toLocaleDateString()}</td>
                        <td className="p-2 uppercase text-sage-accent">{d.kind}</td>
                        <td className="p-2">
                          {d.doc_url
                            ? <a href={d.doc_url} target="_blank" rel="noopener noreferrer" className="text-sage-text hover:text-sage-accent">{d.title}</a>
                            : d.title}
                        </td>
                        <td className="p-2 font-mono text-sage-faint">{d.sha256.slice(0, 16)}…</td>
                        <td className="p-2 text-sage-muted">{d.notary_ref || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* § 06 — REACH OUT · partners / charities / anyone doing the good */}
      {reachOut && <ReachOutBlock reachOut={reachOut} />}

      {/* § 07 — THE FOUNDER'S GUARANTEE · free forever, if you match us */}
      {guarantee && (
        <section className="border-b border-sage-border bg-sage-elevated" data-testid="promises-guarantee">
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 07 · The Founder's Guarantee</div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-sage-text mb-6">
              Match us in philanthropy. Use Sage free, forever.
            </h2>
            <div
              className="border-l-2 border-sage-accent pl-6 py-2 text-lg text-sage-text sage-prose max-w-4xl mb-6"
              data-testid="guarantee-text"
            >
              {guarantee.guarantee}
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent mb-8">
              {guarantee.attribution}
            </div>

            <div className="text-xs uppercase tracking-[0.15em] text-sage-faint mb-3">
              How we know you matched · the criteria
            </div>
            <ol className="text-sm text-sage-muted sage-prose space-y-2 list-decimal ml-6 max-w-3xl">
              {guarantee.match_criteria.map((c, i) => (
                <li key={i} data-testid={`guarantee-criterion-${i}`}>{c}</li>
              ))}
            </ol>
            <div className="mt-8 text-sm text-sage-muted sage-prose max-w-3xl">
              If your company clears all six, send proof through the Reach Out form below. Sage's full product line — the honest chat, the Warden, the Ledger, the Refuge, Baker — becomes free for your team, forever, published below as a signed row. No auto-renew. No small print. No reciprocal advertising required.
            </div>

            <div className="mt-8 border border-sage-accent p-5 bg-sage-accent/5 max-w-4xl" data-testid="flat-salary-covenant">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent font-bold mb-2">
                The Flat Salary Covenant · sixth criterion, added 2026-08-26
              </div>
              <div className="text-sm text-sage-text sage-prose leading-relaxed italic">
                "Every staff member gets the same salary. From Neo the Custodian to The Founder as CEO. We all make (projected) $50K/yr. No one gets paid more than anyone else. The jobs are all necessary. The factory falls apart without a single cog. This is true, for my games, my AI brains, and my Studio. And it always will be."
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mt-3">
                — The Founder
              </div>
              <div className="text-xs text-sage-muted mt-3">
                Match it. Publish your payroll as a flat line. See <a href="/sage-ledger" className="text-sage-accent hover:underline">The Salary Book</a> — the public register where every Resilience paycheck lands as a row. Same amount, every role. Forever.
              </div>
            </div>

            <GuaranteeRosterBlock />
          </div>
        </section>
      )}

      {/* § 08 was previously wired here for a coffin-nail rendering; that
          content was the neighbour house-scope (the Throne of Coffin Nails lives on
          the the neighbour house side, not on Sage's Founding Papers page). Scrubbed
          from Sage on 2026-07-31 (Wave 7 correction). Vault knowledge is
          preserved read-only in family_scrolls/COFFIN_NAILS.md; Sage does
          not render the nails ledger on any public rail. The
          lore_visibility.coffin_nails_public flag remains available as
          forward-looking Sage infrastructure — currently unwired. */}

      <section className="py-12">
        <div className="mx-auto max-w-5xl px-4 md:px-8 text-center">
          <Link
            to="/sage-ledger"
            data-testid="promises-ledger-link"
            className="inline-block border border-sage-accent text-sage-accent px-6 py-3 uppercase tracking-[0.15em] text-xs hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150"
          >
            See it enforced · The Ledger →
          </Link>
        </div>
      </section>
    </div>
  );
}

function GuaranteeRosterBlock() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.guaranteeRoster().then(setD).catch(() => setD({ count: 0, matches: [], policy: "" }));
  }, []);
  if (!d) return null;
  return (
    <div className="mt-12 pt-8 border-t border-sage-border" data-testid="guarantee-roster">
      <div className="flex justify-between items-baseline mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-sage-muted">The Guarantee Roster</div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint tabular-nums">
          {d.count} match{d.count === 1 ? "" : "es"}
        </div>
      </div>
      {d.count === 0 ? (
        <div className="text-sm text-sage-faint sage-prose max-w-3xl">
          Zero companies match today. This row will fill. Watching how fast the number moves is part of the story. The first name on the Roster gets the loudest cell.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {d.matches.map((m) => (
            <a
              key={m.match_id}
              href={m.match_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`roster-match-${m.match_id}`}
              className="border border-sage-border p-4 hover:border-sage-accent transition-colors duration-150 block"
            >
              <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-1">{m.org_name}</div>
              {m.tagline && <div className="text-xs text-sage-muted sage-prose mb-2">{m.tagline}</div>}
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint tabular-nums">
                Matched {new Date(m.matched_at).toLocaleDateString()} · verified by {m.verified_by}
              </div>
            </a>
          ))}
        </div>
      )}
      {d.policy && (
        <div className="mt-6 text-[10px] uppercase tracking-[0.15em] text-sage-faint max-w-3xl leading-relaxed">
          {d.policy}
        </div>
      )}
    </div>
  );
}

// Reach Out block — the Sovereign's partner/charity intake form.
function ReachOutBlock({ reachOut }) {
  const [orgName, setOrgName] = useState("");
  const [kind, setKind] = useState("nonprofit");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [theGood, setTheGood] = useState("");
  const [proposal, setProposal] = useState("");
  const [country, setCountry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!orgName.trim() || !email.trim() || !theGood.trim()) {
      toast.error("Org name, email, and 'the good' are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.reachOutSubmit({
        org_name: orgName, kind, contact_name: contactName || null,
        contact_email: email, website: website || null,
        the_good: theGood, proposal: proposal || null, country: country || null,
      });
      toast.success("Signal received. The Founder reads every one of these.");
      setSent(true);
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <section className="border-b border-sage-border" data-testid="promises-reach-out">
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 06 · Reach out</div>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-sage-text mb-4">
          If you're already doing the good, come find us.
        </h2>
        <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-6 sage-prose" data-testid="reach-out-note">
          {reachOut.note}
        </p>
        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-6">
          Signals received: <span className="text-sage-accent tabular-nums">{reachOut.signals_received}</span>
        </div>

        {sent ? (
          <div className="border border-sage-accent p-6 text-sm text-sage-accent sage-prose" data-testid="reach-out-thanks">
            Thanks. Signal logged. The Founder reads every one of these personally — expect a real human reply, not an autoresponder. If you don't hear back in two weeks, resubmit or nudge the Ledger.
          </div>
        ) : (
          <form onSubmit={submit} className="border border-sage-border p-6 text-xs" data-testid="reach-out-form">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Organisation name *</span>
                <input data-testid="reach-org-name" value={orgName} onChange={(e) => setOrgName(e.target.value)} required className="mt-1 w-full bg-sage-elevated border border-sage-border px-3 py-2 text-sage-text" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Kind</span>
                <select data-testid="reach-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1 w-full bg-sage-elevated border border-sage-border px-3 py-2 text-sage-text">
                  <option value="company">Company</option>
                  <option value="nonprofit">Nonprofit / charity</option>
                  <option value="individual">Individual</option>
                  <option value="government">Government / NGO</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Your name</span>
                <input data-testid="reach-contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)} className="mt-1 w-full bg-sage-elevated border border-sage-border px-3 py-2 text-sage-text" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Contact email *</span>
                <input data-testid="reach-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full bg-sage-elevated border border-sage-border px-3 py-2 text-sage-text" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Website</span>
                <input data-testid="reach-website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" className="mt-1 w-full bg-sage-elevated border border-sage-border px-3 py-2 text-sage-text" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Country</span>
                <input data-testid="reach-country" value={country} onChange={(e) => setCountry(e.target.value)} className="mt-1 w-full bg-sage-elevated border border-sage-border px-3 py-2 text-sage-text" />
              </label>
            </div>
            <label className="block mb-4">
              <span className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">The good you're already doing *</span>
              <textarea data-testid="reach-the-good" value={theGood} onChange={(e) => setTheGood(e.target.value)} required rows={3} className="mt-1 w-full bg-sage-elevated border border-sage-border px-3 py-2 text-sage-text sage-prose" />
            </label>
            <label className="block mb-4">
              <span className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Proposal · what you'd like to do with Sage</span>
              <textarea data-testid="reach-proposal" value={proposal} onChange={(e) => setProposal(e.target.value)} rows={3} className="mt-1 w-full bg-sage-elevated border border-sage-border px-3 py-2 text-sage-text sage-prose" />
            </label>
            <button
              type="submit"
              data-testid="reach-submit"
              disabled={submitting}
              className="border border-sage-accent text-sage-accent px-6 py-2 uppercase tracking-[0.15em] text-xs hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-50"
            >
              {submitting ? "…" : "Send signal"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
